import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null = checking, false = logged out, object = logged in
    const [sessionExpired, setSessionExpired] = useState(false);
    const userRef = useRef(null);
    userRef.current = user;

    useEffect(() => {
        const onExpired = () => {
            if (!userRef.current) return;
            userRef.current = false;
            api.post("/auth/logout").catch(() => {});
            setSessionExpired(true);
            setUser(false);
            toast.info("Your session expired. Please sign in again.", { id: "session-expired" });
        };
        window.addEventListener("ares:session-expired", onExpired);
        return () => window.removeEventListener("ares:session-expired", onExpired);
    }, []);

    const checkSession = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch (e) {
            setUser(false);
        }
    }, []);

    useEffect(() => {
        checkSession();
    }, [checkSession]);

    const login = async (email, password) => {
        try {
            const { data } = await api.post("/auth/login", { email, password });
            setSessionExpired(false);
            setUser(data);
            return { ok: true };
        } catch (e) {
            return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
        }
    };

    const register = async (name, email, password) => {
        try {
            const { data } = await api.post("/auth/register", { name, email, password });
            setUser(data);
            return { ok: true };
        } catch (e) {
            return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
        }
    };

    const logout = async () => {
        try { await api.post("/auth/logout"); } catch (e) { /* ignore */ }
        setUser(false);
    };

    return (
        <AuthContext.Provider value={{ user, login, register, logout, sessionExpired }}>
            {children}
        </AuthContext.Provider>
    );
}
